// components/ExclusionsModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    TextField,
    MenuItem,
    Stack,
    Tooltip,
    Box,
    Divider,
    Chip,
    InputAdornment,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    FormControl,
    InputLabel,
    Select,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useAuth } from "./TokenHandler";

/* ---------- Config & helpers ---------- */
const API_BASE = process.env.REACT_APP_API_URL;
const LOG = true; // flip to false to silence logs
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

async function readAsJsonWithLogs(res) {
    const raw = await res.text();
    if (LOG) console.log("[Exclusions] raw:", raw);
    try {
        return JSON.parse(raw);
    } catch {
        throw new Error(`Bad JSON (status ${res.status})`);
    }
}

/* ---------- Row Update mini-dialog ---------- */
const UpdateRowDialog = ({ open, onClose, row, onSubmit, submitting }) => {
    const [title, setTitle] = useState(row?.title || "");
    const [notes, setNotes] = useState(row?.notes || "");
    const [hide, setHide] = useState(!!row?.hide);
    const [forward, setForward] = useState(!!row?.forward);
    const [forwardTo, setForwardTo] = useState(row?.forward_to || "");

    useEffect(() => {
        setTitle(row?.title || "");
        setNotes(row?.notes || "");
        setHide(!!row?.hide);
        setForward(!!row?.forward);
        setForwardTo(row?.forward_to || "");
    }, [row]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                Update Exclusion
                <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth />
                    <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} multiline minRows={2} fullWidth />
                    <TextField select label="Hide" value={hide ? "true" : "false"} onChange={(e) => setHide(e.target.value === "true")} fullWidth>
                        <MenuItem value="false">No</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                    </TextField>
                    <TextField select label="Forward" value={forward ? "true" : "false"} onChange={(e) => setForward(e.target.value === "true")} fullWidth>
                        <MenuItem value="false">No</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                    </TextField>
                    <TextField label="Forward To" value={forwardTo} onChange={(e) => setForwardTo(e.target.value)} helperText="Optional: email, group, or destination" fullWidth />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={() =>
                        onSubmit({
                            title: title?.trim(),
                            notes: notes?.trim(),
                            hide,
                            forward,
                            forward_to: forwardTo?.trim() || null,
                        })
                    }
                    disabled={submitting || !title?.trim()}
                    startIcon={submitting ? <CircularProgress size={18} /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/* ---------- Main Modal ---------- */
const ExclusionsModal = ({ open, onClose }) => {
    const { keycloak: kc } = useAuth();

    // Filters/Query params
    const [active, setActive] = useState("true"); // "true" | "false" | "all"
    const [search, setSearch] = useState("");
    const [updatedFrom, setUpdatedFrom] = useState(null);
    const [orderBy, setOrderBy] = useState("updated_on_desc");

    // Paging
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);

    // Data
    const [rows, setRows] = useState([]);
    const [pageCount, setPageCount] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // Update dialog state
    const [editingRow, setEditingRow] = useState(null);
    const [saving, setSaving] = useState(false);

    // reload trigger
    const [refreshTick, setRefreshTick] = useState(0);

    const token = kc?.token;

    // Compose querystring based on filters
    const qs = useMemo(() => {
        const p = new URLSearchParams();
        if (active !== "all") p.set("active", active);
        if (search?.trim()) p.set("search", search.trim());
        if (updatedFrom) p.set("updated_from", dayjs(updatedFrom).format("YYYY-MM-DD"));
        if (orderBy) p.set("orderBy", orderBy);
        if (page) p.set("page", String(page));
        if (pageSize) p.set("pageSize", String(pageSize));
        return p.toString();
    }, [active, search, updatedFrom, orderBy, page, pageSize]);

    // Fetch exclusions when modal opens or filters change
    useEffect(() => {
        if (!open) return;
        let cancel = false;

        (async () => {
            setLoading(true);
            setErr("");
            const url = `${API_BASE}/facility/exclusions?${qs}`;
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            try {
                if (LOG) console.log("[Exclusions] GET", { url, headers });
                const res = await fetch(url, { headers });
                if (!res.ok) {
                    const raw = await res.text();
                    if (LOG) console.error("[Exclusions] error raw:", raw);
                    throw new Error(raw || `HTTP ${res.status}`);
                }

                const json = await readAsJsonWithLogs(res);
                if (LOG) console.log("[Exclusions] parsed:", json);

                // Your API shape: { rows: [...], pagination: { total, page, pageSize, totalPages, ... } }
                const items = Array.isArray(json?.rows)
                    ? json.rows
                    : Array.isArray(json?.items)
                        ? json.items
                        : Array.isArray(json)
                            ? json
                            : [];

                const totalPages =
                    json?.pagination?.totalPages ??
                    json?.totalPages ??
                    (json?.pagination?.total && json?.pagination?.pageSize
                        ? Math.max(1, Math.ceil(json.pagination.total / json.pagination.pageSize))
                        : 1);

                if (!cancel) {
                    setRows(items);
                    setPageCount(totalPages);
                    setTotal(json?.pagination?.total ?? items.length ?? 0);
                    // keep server-provided page/pageSize in sync if returned
                    if (json?.pagination?.page) setPage(Number(json.pagination.page));
                    if (json?.pagination?.pageSize) setPageSize(Number(json.pagination.pageSize));
                }
            } catch (e) {
                if (!cancel) {
                    setErr(e?.message || "Failed to load exclusions.");
                    setRows([]);
                    setPageCount(1);
                    setTotal(0);
                }
            } finally {
                if (!cancel) setLoading(false);
            }
        })();

        return () => {
            cancel = true;
        };
    }, [open, qs, token, refreshTick]);

    const reload = () => setRefreshTick((n) => n + 1);

    // Batch helpers
    const postBatch = async (items) => {
        const url = `${API_BASE}/facility/exclusions/batch`;
        const body = JSON.stringify({ items });
        const headers = {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
        if (LOG) console.log("[Exclusions] BATCH POST", { url, headers, body: JSON.parse(body) });
        const res = await fetch(url, { method: "POST", headers, body });
        const raw = await res.text();
        if (LOG) {
            console.log("[Exclusions] BATCH status:", res.status);
            console.log("[Exclusions] BATCH raw:", raw);
        }
        if (!res.ok) throw new Error(raw || `HTTP ${res.status}`);
        return JSON.parse(raw);
    };

    const onDeactivate = async (row) => {
        try {
            setSaving(true);
            await postBatch([
                {
                    id: row.id ?? null,
                    facility_unique_id: row.facility_unique_id ?? null,
                    action: "deactivate",
                },
            ]);
            reload();
        } catch (e) {
            alert(e?.message || "Deactivate failed");
        } finally {
            setSaving(false);
        }
    };

    const onSubmitUpdate = async (payload) => {
        if (!editingRow) return;
        try {
            setSaving(true);
            await postBatch([
                {
                    id: editingRow.id ?? null,
                    facility_unique_id: editingRow.facility_unique_id ?? null,
                    action: "update",
                    title: payload.title,
                    notes: payload.notes,
                    hide: payload.hide,
                    forward: payload.forward,
                    forward_to: payload.forward_to,
                },
            ]);
            setEditingRow(null);
            reload();
        } catch (e) {
            alert(e?.message || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    /* -------- toolbar -------- */
    const toolbar = (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
                select
                label="Active"
                value={active}
                onChange={(e) => {
                    setPage(1);
                    setActive(e.target.value);
                }}
                size="small"
                sx={{ width: 140 }}
            >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
                <MenuItem value="all">All</MenuItem>
            </TextField>

            <TextField
                label="Search"
                value={search}
                onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                }}
                size="small"
                sx={{ minWidth: 260 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                        </InputAdornment>
                    ),
                }}
                placeholder="Facility name, title, notes…"
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    label="Updated From"
                    slotProps={{ textField: { size: "small" } }}
                    value={updatedFrom}
                    onChange={(v) => {
                        setPage(1);
                        setUpdatedFrom(v);
                    }}
                />
            </LocalizationProvider>

            <TextField
                select
                label="Order By"
                value={orderBy}
                onChange={(e) => {
                    setPage(1);
                    setOrderBy(e.target.value);
                }}
                size="small"
                sx={{ width: 220 }}
            >
                <MenuItem value="updated_on_desc">Updated (new → old)</MenuItem>
                <MenuItem value="updated_on_asc">Updated (old → new)</MenuItem>
                <MenuItem value="facility_asc">Facility (A → Z)</MenuItem>
                <MenuItem value="facility_desc">Facility (Z → A)</MenuItem>
            </TextField>
        </Stack>
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Exclusions
                    {active === "false" ? (
                        <Chip color="warning" size="small" label="Showing inactive" />
                    ) : active === "true" ? (
                        <Chip color="success" size="small" label="Showing active" />
                    ) : (
                        <Chip size="small" label="Showing all" />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {toolbar}
                    <Divider sx={{ mb: 2 }} />

                    {err && (
                        <Box sx={{ color: "error.main", mb: 2, fontSize: 14 }}>{err}</Box>
                    )}

                    <Box sx={{ position: "relative", minHeight: 200 }}>
                        {loading && (
                            <Stack
                                alignItems="center"
                                justifyContent="center"
                                sx={{ position: "absolute", inset: 0 }}
                            >
                                <CircularProgress />
                            </Stack>
                        )}

                        {!loading && rows.length === 0 && (
                            <Box sx={{ p: 2, color: "text.secondary" }}>
                                No exclusions found.
                            </Box>
                        )}

                        {!loading && rows.length > 0 && (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Facility</TableCell>
                                        <TableCell>Facility ID</TableCell>
                                        <TableCell>Title</TableCell>
                                        <TableCell>Notes</TableCell>
                                        <TableCell>Active</TableCell>
                                        <TableCell>Updated On</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((r) => (
                                        <TableRow key={r.id || `${r.facility_unique_id}-${r.title}`}>
                                            <TableCell>{r.facility_name || "-"}</TableCell>
                                            <TableCell>{r.facility_unique_id ?? "-"}</TableCell>
                                            <TableCell>{r.title || "-"}</TableCell>
                                            <TableCell sx={{ maxWidth: 420, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                                                {r.notes || ""}
                                            </TableCell>
                                            <TableCell>
                                                {String(r.active ?? true) === "true" ? (
                                                    <Chip size="small" color="success" label="Active" />
                                                ) : (
                                                    <Chip size="small" label="Inactive" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {r.updated_on ? dayjs(r.updated_on).format("YYYY-MM-DD HH:mm") : "-"}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    <Tooltip title="Update">
                            <span>
                              <IconButton size="small" onClick={() => setEditingRow(r)} disabled={saving}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                                                    </Tooltip>

                                                    {String(r.active ?? true) === "true" && (
                                                        <Tooltip title="Deactivate">
                              <span>
                                <IconButton size="small" onClick={() => onDeactivate(r)} disabled={saving}>
                                  <DeleteForeverIcon fontSize="small" />
                                </IconButton>
                              </span>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                </DialogContent>

                {/* Footer with ProcessChanges-style pagination controls */}
                <DialogActions
                    sx={{
                        justifyContent: "space-between",
                        px: 2,
                        py: 1,
                        borderTop: (t) => `1px solid ${t.palette.divider}`,
                        // keep any global Pagination overrides from leaking in
                        "& .MuiPagination-root": { position: "static", zIndex: "auto" },
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                        <IconButton
                            onClick={() => {
                                if (page <= 1) return;
                                setPage((p) => p - 1);
                            }}
                            disabled={page <= 1}
                            size="small"
                        >
                            <NavigateBeforeIcon />
                        </IconButton>

                        <IconButton
                            onClick={() => {
                                if (page >= pageCount) return;
                                setPage((p) => p + 1);
                            }}
                            disabled={page >= pageCount}
                            size="small"
                        >
                            <NavigateNextIcon />
                        </IconButton>

                        <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                            Page {page} of {pageCount}
                        </Typography>

                        <FormControl size="small" sx={{ minWidth: 96 }}>
                            <InputLabel id="rows-per-page-label">Rows</InputLabel>
                            <Select
                                labelId="rows-per-page-label"
                                label="Rows"
                                value={pageSize}
                                onChange={(e) => {
                                    const newSize = Number(e.target.value) || 100;
                                    setPage(1);
                                    setPageSize(newSize);
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt} value={opt}>
                                        {opt}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography variant="body2" color="text.secondary">
                            Total: {total.toLocaleString()}
                        </Typography>
                    </Stack>

                    <Button onClick={onClose} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Update dialog */}
            <UpdateRowDialog
                open={!!editingRow}
                onClose={() => setEditingRow(null)}
                row={editingRow}
                onSubmit={onSubmitUpdate}
                submitting={saving}
            />
        </>
    );
};

export default ExclusionsModal;
